import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TSP } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly http = inject(HttpClient);

  public runMigration(database: string): Observable<string[]> {
    return this.http.post<string[]>(`/admin/database/run-migration/${database}`, {});
  }

  public clearCache(service: string, key: string): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>(`/admin/cache/clear-cache/${service}/${key}`, {});
  }

  public classifyFiles(
    portalId: number,
    limit: number,
    forceProcessing: boolean,
  ): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/file/classify-files', {
      portalId,
      limit,
      forceProcessing,
    });
  }

  public vectorizeTextContent(portalId: number, limit: number): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/file/vectorize-text-content', { portalId, limit });
  }

  public getPortalFilesStatistic(
    portalId: number,
    fileType: TSP.Internal.File.File.Type[],
    fileStatus: TSP.Internal.File.File.ProcessingStatus[],
  ): Observable<TSP.Internal.File.File.StatisticResponse> {
    let params = new HttpParams().set('portalId', portalId);
    fileType.forEach((type) => (params = params.append('fileType', type)));
    fileStatus.forEach((status) => (params = params.append('fileStatus', status)));

    return this.http.get<TSP.Internal.File.File.StatisticResponse>('/admin/file/get-portal-files-statistic', {
      params,
    });
  }

  /**
   * `portalId` is sent explicitly. The gateway used to read it off the caller's
   * token, so this ran against whichever portal the admin belonged to and the
   * portal picker on the page had no effect on it.
   */
  public processOrphanTextContent(
    type: TSP.Internal.File.AI.OrphanType,
    portalId: number,
  ): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>(
      `/admin/file/vectorize-text-content/process-orphans/${type}`,
      { portalId },
    );
  }

  public processExistedBatch(
    batchId: string,
    provider: 'openai' | 'anthropic',
  ): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/file/vectorize-text-content/process-existed-batch', {
      batchId,
      provider,
    });
  }

  public fixPhones(): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>('/admin/contacts/fix-phones', {});
  }

  public fixSocialLinks(): Observable<{ deleted: number }> {
    return this.http.post<{ deleted: number }>('/admin/contacts/fix-social-links', {});
  }

  /**
   * The competence i18n pass, which runs on ai-service.
   *
   * ai-service is not reachable from outside the cluster and has no gateway
   * surface of its own, so before these routes existed the only way to drive a
   * paid, irreversible taxonomy-wide run was a shell inside a container.
   */
  public getCompetenceI18nStatus(): Observable<TSP.Internal.Ai.CompetenceI18nStatus> {
    return this.http.get<TSP.Internal.Ai.CompetenceI18nStatus>('/admin/competence/i18n-status');
  }

  public previewCompetenceTranslations(limit: number): Observable<TSP.Internal.Ai.CompetenceTranslationPreview[]> {
    return this.http.post<TSP.Internal.Ai.CompetenceTranslationPreview[]>('/admin/competence/translate/preview', {
      limit,
    });
  }

  public translateCompetences(limit?: number): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/competence/translate', { limit });
  }

  public proposeCompetenceI18nMerges(limit?: number): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/competence/propose-i18n-merges', { limit });
  }

  public curateCompetences(limit?: number): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/competence/curate', { limit });
  }

  public refreshCompetenceProfileCounts(): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.post<TSP.API.Helpers.BooleanResponse>('/admin/competence/refresh-profile-counts', {});
  }

  /** One pass over open vacancies whose competences were never extracted; `result` is how many it filled. */
  public backfillVacancyCompetences(limit?: number): Observable<TSP.Helpers.NumericResponse> {
    return this.http.post<TSP.Helpers.NumericResponse>('/admin/vacancy/competences/backfill', { limit });
  }

  /** One pass over open vacancies with no sourcing intent yet; `result` is how many it filled. */
  public backfillVacancySourcingIntent(limit?: number): Observable<TSP.Helpers.NumericResponse> {
    return this.http.post<TSP.Helpers.NumericResponse>('/admin/vacancy/sourcing-intent/backfill', { limit });
  }
}
